import type { Locale } from '../types'

/**
 * Client Review — Revision 01. Highest-priority layer in the single `t()` lookup
 * (LocaleContext), so these keys supersede earlier copy without editing it in place.
 * Any "XP" written here is rendered as "Bee" by the terminology layer.
 */
export const review01Strings: Record<Locale, Record<string, string>> = {
  'pt-BR': {
    /* Habits */
    'habit.mind.goal': 'Um momento seu para você.',
    'habit.mind.examples': 'Meditação · leitura · música',
    'habit.sleep.goal': 'Dormi bem',

    /* Drained mood — gentle goals (nutrition + personal recovery) */
    'home.gentle.2': 'Escolha uma refeição nutritiva e leve.',
    'home.gentle.3': 'Reserve um momento seu para se recuperar.',
    'home.gentle.4': 'Priorize dormir mais cedo.',

    /* Sports catalogue */
    'category.crossfit': 'CrossFit / Funcional',
    'category.triathlon': 'Triatlo',
    'category.yoga': 'Yoga',
    'category.pilates': 'Pilates',
    'category.beachTennis': 'Beach Tennis',
    'category.jiujitsu': 'Jiu-jitsu',
    'category.muayThai': 'Muay Thai',
    'category.boxing': 'Boxe',
    'onb.act.crossfit': 'CrossFit / Funcional',
    'onb.act.yoga': 'Yoga',
    'onb.act.pilates': 'Pilates',
    'onb.act.beachTennis': 'Beach Tennis',
    'onb.act.jiujitsu': 'Jiu-jitsu',
    'onb.act.muayThai': 'Muay Thai',
    'onb.act.boxing': 'Boxe',

    /* Body progress chart */
    'metrics.charts.view.label': 'Métrica de evolução',
    'metrics.charts.view.composition': 'Composição corporal',
    'metrics.charts.view.weight': 'Peso',
    'metrics.charts.bodyNote':
      'A composição corporal combina apenas FFM, gordura e água. Peso aparece como métrica própria; BMR tem leitura própria.',

    /* Sport-metric evolution */
    'metrics.sports.evolution.title': 'Evolução',
    'metrics.sports.evolution.choose': 'Escolha a métrica',
    'metrics.sports.evolution.question': 'Como esta métrica do seu esporte evoluiu ao longo do tempo?',
    'metrics.sports.evolution.empty': 'Este esporte ainda não tem métricas com histórico registrado.',

    /* Weekly training frequency goal */
    'goals.training.title': 'Frequência de treino',
    'goals.training.weekly': '{n} treinos por semana',
    'goals.training.weeklyOne': '1 treino por semana',
    'goals.training.yearly': 'Equivale a {n} treinos no ano',
    'goals.training.progress': '{done} / {target} treinos em {year}',
    'goals.training.change': 'Nova meta semanal',
    'goals.training.decrease': 'Diminuir meta semanal',
    'goals.training.increase': 'Aumentar meta semanal',
    'goals.training.perWeek': 'por semana',
    'goals.training.apply': 'Aplicar a partir de hoje',
    'goals.training.applied': 'Nova meta válida desde {date}.',
    'goals.training.history': 'Histórico da meta',
    'goals.training.period.open': '{n} por semana · desde {from}',
    'goals.training.period.closed': '{n} por semana · {from} – {to}',
    'goals.training.note':
      'Mudanças valem a partir da data da alteração e não reescrevem metas anteriores. A meta do ano soma cada período de forma proporcional.',
  },
  'en-US': {
    'habit.mind.goal': 'A moment of your own, for you.',
    'habit.mind.examples': 'Meditation · reading · music',
    'habit.sleep.goal': 'I slept well',

    'home.gentle.2': 'Choose a nourishing, light meal.',
    'home.gentle.3': 'Set aside a moment of your own to recover.',
    'home.gentle.4': 'Prioritize an earlier night.',

    'category.crossfit': 'CrossFit / Functional',
    'category.triathlon': 'Triathlon',
    'category.yoga': 'Yoga',
    'category.pilates': 'Pilates',
    'category.beachTennis': 'Beach Tennis',
    'category.jiujitsu': 'Jiu-jitsu',
    'category.muayThai': 'Muay Thai',
    'category.boxing': 'Boxing',
    'onb.act.crossfit': 'CrossFit / Functional',
    'onb.act.yoga': 'Yoga',
    'onb.act.pilates': 'Pilates',
    'onb.act.beachTennis': 'Beach Tennis',
    'onb.act.jiujitsu': 'Jiu-jitsu',
    'onb.act.muayThai': 'Muay Thai',
    'onb.act.boxing': 'Boxing',

    'metrics.charts.view.label': 'Evolution metric',
    'metrics.charts.view.composition': 'Body composition',
    'metrics.charts.view.weight': 'Weight',
    'metrics.charts.bodyNote':
      'Body composition combines only FFM, body fat and water. Weight is its own metric; BMR has its own readout.',

    'metrics.sports.evolution.title': 'Evolution',
    'metrics.sports.evolution.choose': 'Choose a metric',
    'metrics.sports.evolution.question': 'How has this metric for your sport changed over time?',
    'metrics.sports.evolution.empty': 'This sport has no metrics with recorded history yet.',

    'goals.training.title': 'Training frequency',
    'goals.training.weekly': '{n} workouts per week',
    'goals.training.weeklyOne': '1 workout per week',
    'goals.training.yearly': 'Equals {n} workouts a year',
    'goals.training.progress': '{done} / {target} workouts in {year}',
    'goals.training.change': 'New weekly goal',
    'goals.training.decrease': 'Decrease weekly goal',
    'goals.training.increase': 'Increase weekly goal',
    'goals.training.perWeek': 'per week',
    'goals.training.apply': 'Apply from today',
    'goals.training.applied': 'New goal effective from {date}.',
    'goals.training.history': 'Goal history',
    'goals.training.period.open': '{n} per week · since {from}',
    'goals.training.period.closed': '{n} per week · {from} – {to}',
    'goals.training.note':
      'Changes apply from the date they are made and never rewrite earlier goals. The yearly goal adds up each period proportionally.',
  },
}
